import { ERole } from '../../features/auth/enums/role.enum';

export type AppSubdomain = 'family' | 'school' | 'admin' | 'patrol';

export interface AppDestination {
  subdomain: AppSubdomain;
  path: string;
}

/** Preferred home for a user based on JWT roles (highest-priority match first). */
export function destinationForRoles(roles: string[]): AppDestination | null {
  if (roles.includes(ERole.ADMIN)) {
    return { subdomain: 'admin', path: '/office' };
  }
  if (roles.includes(ERole.PATROL)) {
    return { subdomain: 'patrol', path: '/setup' };
  }
  if (roles.includes(ERole.TEACHER) || roles.includes(ERole.DIRECTOR) || roles.includes(ERole.SCHOOL)) {
    return { subdomain: 'school', path: '/courses' };
  }
  if (roles.includes(ERole.PARENT)) {
    return { subdomain: 'family', path: '/home' };
  }
  return null;
}

export function currentSubdomain(hostname = window.location.hostname): string {
  return hostname.split('.')[0] ?? '';
}

/** Build absolute URL for another app subdomain on the same root domain. */
export function urlForSubdomain(
  subdomain: AppSubdomain,
  path: string,
  hostname = window.location.hostname,
): string {
  const parts = hostname.split('.');
  if (parts.length < 2) {
    // Local single-host fallback: stay on current origin.
    return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
  }
  parts[0] = subdomain;
  const host = parts.join('.');
  const { protocol, port } = window.location;
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${protocol}//${host}${portSuffix}${normalizedPath}`;
}

export function navigateToDestination(destination: AppDestination): void {
  if (currentSubdomain() === destination.subdomain) {
    void window.location.assign(destination.path);
    return;
  }
  window.location.assign(
    urlForSubdomain(destination.subdomain, destination.path),
  );
}
